// src/lib/api.ts
// Typed API client — handles auth headers, token refresh, and errors

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:4000'

let accessToken: string | null = null

export function setAccessToken(token: string | null) {
  accessToken = token
}

export function getAccessToken() {
  return accessToken
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────
async function apiFetch<T>(
  path: string,
  options: RequestInit = {},
  retry = true
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include', // send refresh token cookie
  })

  // Auto-refresh on 401
  if (res.status === 401 && retry) {
    const refreshed = await tryRefresh()
    if (refreshed) {
      return apiFetch<T>(path, options, false)
    }
    // Refresh failed — clear local auth state
    accessToken = null
    throw new ApiError(401, 'Session expired')
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`
    try {
      const body = await res.json()
      message = body.error ?? message
    } catch {}
    throw new ApiError(res.status, message)
  }

  // 204 No Content
  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

async function tryRefresh(): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })
    if (!res.ok) return false
    const { accessToken: newToken } = await res.json()
    accessToken = newToken
    return true
  } catch {
    return false
  }
}

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'ApiError'
  }
}

// ── Auth endpoints ────────────────────────────────────────────────────────────
export interface AuthUser {
  id: string
  email: string
  name: string
  role: 'fan' | 'admin'
}

export interface AuthResponse {
  user: AuthUser
  accessToken: string
}

export const auth = {
  signUp: (data: { name: string; email: string; password: string }) =>
    apiFetch<AuthResponse>('/api/auth/signup', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  signIn: (data: { email: string; password: string }) =>
    apiFetch<AuthResponse>('/api/auth/signin', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  signOut: () =>
    apiFetch<{ ok: boolean }>('/api/auth/signout', { method: 'POST' }),

  me: () => apiFetch<{ user: AuthUser }>('/api/auth/me'),
}

// ── Booking endpoint ──────────────────────────────────────────────────────────
export interface BookingPayload {
  name: string
  email: string
  phone?: string
  eventType: string
  eventDate?: string
  venue?: string
  city?: string
  message: string
}

export const bookings = {
  submit: (data: BookingPayload) =>
    apiFetch<{ ok: boolean; bookingId: string; message: string }>('/api/bookings', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
}

// ── Admin endpoints ───────────────────────────────────────────────────────────
export interface Booking {
  id: string
  name: string
  email: string
  phone: string | null
  event_type: string
  event_date: string | null
  venue: string | null
  city: string | null
  message: string
  status: 'pending' | 'reviewing' | 'confirmed' | 'declined'
  admin_notes: string | null
  created_at: string
  updated_at: string
}

export interface Pagination {
  total: number
  page: number
  limit: number
  totalPages: number
}

export const admin = {
  getBookings: (params?: { status?: string; page?: number; search?: string }) => {
    const qs = new URLSearchParams()
    if (params?.status) qs.set('status', params.status)
    if (params?.page) qs.set('page', String(params.page))
    if (params?.search) qs.set('search', params.search)
    return apiFetch<{ bookings: Booking[]; pagination: Pagination }>(
      `/api/admin/bookings?${qs}`
    )
  },

  getBooking: (id: string) =>
    apiFetch<{ booking: Booking }>(`/api/admin/bookings/${id}`),

  updateBooking: (id: string, data: { status?: string; adminNotes?: string }) =>
    apiFetch<{ booking: Booking }>(`/api/admin/bookings/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteBooking: (id: string) =>
    apiFetch<{ ok: boolean }>(`/api/admin/bookings/${id}`, { method: 'DELETE' }),

  getStats: () =>
    apiFetch<{
      totalUsers: number
      totalBookings: number
      bookingsByStatus: Record<string, number>
      recentBookings: Booking[]
    }>('/api/admin/stats'),

  getUsers: (page = 1) =>
    apiFetch<{ users: AuthUser[]; pagination: Pagination }>(
      `/api/admin/users?page=${page}`
    ),

  updateUserRole: (id: string, role: 'fan' | 'admin') =>
    apiFetch<{ user: AuthUser }>(`/api/admin/users/${id}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    }),
}
